'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

type GlobalUiType = {
  headerCollaspable?: boolean;
  setHeaderCollaspable: (value: boolean) => void;

  headerVisible?: boolean;
  setHeaderVisible: (value: boolean) => void;

  headerHeight?: number;
  setHeaderHeight: (value: number) => void;

  liteUI?: boolean;
  setLiteUI: (value: boolean) => void;

  footerDisabled?: boolean;
  setFooterDisabled: (value: boolean) => void;

  /** Runtime header-theme override — a page flips the nav as a section scrolls
   *  (e.g. /science: dark over the hero, light once it leaves). `null` = no
   *  override → the route-derived theme. Cleared on navigation. */
  headerThemeOverride?: 'dark' | 'light' | null;
  setHeaderThemeOverride: (value: 'dark' | 'light' | null) => void;

  /** Pages call this (via useGlobalUI) with their pathname to keep their UI
   *  flags from being reset by the provider's on-navigation cleanup. */
  claimNavigation: (pathname: string) => void;
};

export const GlobalUIContext = createContext<GlobalUiType>({
  headerCollaspable: false,
  setHeaderCollaspable: () => undefined,

  headerVisible: true,
  setHeaderVisible: () => undefined,

  // 76 = mobile bar (44) + marketing strip (32). Header.tsx republishes the
  // real value per breakpoint / scroll state on mount.
  headerHeight: 76,
  setHeaderHeight: () => undefined,

  liteUI: false,
  setLiteUI: () => undefined,

  footerDisabled: false,
  setFooterDisabled: () => undefined,

  headerThemeOverride: null,
  setHeaderThemeOverride: () => undefined,

  claimNavigation: () => undefined,
});

export const GlobalUIProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const pathname = usePathname();
  const [headerCollaspable, setHeaderCollaspable] = useState<boolean>(false);
  const [liteUI, setLiteUI] = useState<boolean>(false);
  const [headerHeight, setHeaderHeight] = useState<number>(76);
  const [headerVisible, setHeaderVisible] = useState<boolean>(true);
  const [footerDisabled, setFooterDisabled] = useState<boolean>(false);
  const [headerThemeOverride, setHeaderThemeOverride] = useState<'dark' | 'light' | null>(
    null,
  );
  const claimedForPath = useRef<string | null>(null);

  useEffect(() => {
    // Reset to defaults on navigation — unless the destination page declared
    // its own flags for this pathname (its useGlobalUI effect runs before this
    // one, child effects fire before parent effects).
    if (claimedForPath.current !== pathname) {
      setHeaderCollaspable((prev) => (prev !== false ? false : prev));
      setLiteUI((prev) => (prev !== false ? false : prev));
      setHeaderVisible((prev) => (prev !== true ? true : prev));
      setFooterDisabled((prev) => (prev !== false ? false : prev));
      setHeaderThemeOverride((prev) => (prev !== null ? null : prev));
    }
  }, [pathname]);

  const value = useMemo(
    () =>
      ({
        headerCollaspable,
        setHeaderCollaspable,

        headerVisible,
        setHeaderVisible,

        headerHeight,
        setHeaderHeight,

        liteUI,
        setLiteUI,

        footerDisabled,
        setFooterDisabled,

        headerThemeOverride,
        setHeaderThemeOverride,

        claimNavigation: (path: string) => {
          claimedForPath.current = path;
        },
      } as const),
    [
      headerCollaspable,
      setHeaderCollaspable,

      headerVisible,
      setHeaderVisible,

      headerHeight,

      liteUI,
      setLiteUI,

      footerDisabled,
      setFooterDisabled,

      headerThemeOverride,
    ],
  );

  return (
    <GlobalUIContext.Provider value={value}>
      {children}
    </GlobalUIContext.Provider>
  );
};

export const useGlobalUI = (
  params?:
    | {
        headerCollaspable?: boolean;
        liteUI?: boolean;
        footerDisabled?: boolean;
      }
    | undefined,
) => {
  const GlobalUIClient = useContext(GlobalUIContext);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams?.toString() ?? '';
  useEffect(() => {
    if (params) {
      GlobalUIClient.claimNavigation(pathname ?? '/');
      const liteUI = params.liteUI ? params.liteUI : false;
      if (GlobalUIClient.liteUI !== liteUI) GlobalUIClient.setLiteUI(liteUI);

      const headerCollaspable = params.headerCollaspable
        ? params.headerCollaspable
        : false;
      if (GlobalUIClient.headerCollaspable !== headerCollaspable)
        GlobalUIClient.setHeaderCollaspable(headerCollaspable);

      const footerDisabled = params.footerDisabled ? true : false;
      if (GlobalUIClient.footerDisabled !== footerDisabled)
        GlobalUIClient.setFooterDisabled(footerDisabled);
    } else {
      if (GlobalUIClient.liteUI !== false) GlobalUIClient.setLiteUI(false);
      if (GlobalUIClient.headerCollaspable !== false)
        GlobalUIClient.setHeaderCollaspable(false);
      if (GlobalUIClient.footerDisabled !== false)
        GlobalUIClient.setFooterDisabled(false);
    }
  }, [params, pathname, search]);

  return GlobalUIClient;
};
